import { baseApi } from "@/services/api";
import { DELETE_CONFIRM_TOKEN } from "@/features/catalog-lifecycle/catalogLifecycleTypes";
import type {
  CatalogPurgeCount,
  CatalogPurgePreflight,
} from "@/features/catalog-lifecycle/catalogLifecycleTypes";

// V2 Music Catalog · Genre / Subgenre taxonomy API client (owner-approved v1).
//
// Backed by kk-admin-sa-music-catalog through `/genres`. Reads come from the
// genre/subgenre PROJECTION tables (DDB-stream materialized), so an instant
// refetch after ANY mutation can return a stale list that still shows the
// pre-mutation state until the materializer catches up (owner QA finding
// 2026-08-29: "Dark House" subgenre create not visible until browser
// refresh). EVERY lifecycle mutation (create / rename / archive / restore /
// permanent delete, genre + subgenre) therefore patches every cached list
// from the canonical mutation response immediately and reconciles with a
// DELAYED refetch once the projection has had time to settle.

export type GenreStatus = "active" | "deleted";

export interface Subgenre {
  subgenreId: string;
  genreId: string;
  name: string;
  nameLower: string;
  status: GenreStatus;
  /** Living Songs referencing this subgenre (present when counts=true). */
  songCount?: number;
  createdAt: string;
  updatedAt: string;
  version: number;
}

export interface Genre {
  genreId: string;
  name: string;
  nameLower: string;
  status: GenreStatus;
  /** Living Songs referencing this genre (present when counts=true). */
  songCount?: number;
  createdAt: string;
  updatedAt: string;
  version: number;
  /** Subgenres of ALL statuses, sorted by nameLower. */
  subgenres?: Subgenre[];
}

export interface ListGenresArgs {
  status?: GenreStatus | "all";
  /** true for the management page (songCount), false for picker dropdowns. */
  counts?: boolean;
  limit?: number;
  pageToken?: string;
}

export interface ListGenresResponse {
  genres: Genre[];
  status: string;
  limit: number;
  pageToken: string | null;
}

/**
 * Genre/Subgenre purge preflight — same shape family as the record-labels
 * purge preflight, plus taxonomy-specific detail counts. `blocked` is the
 * wire spelling; `eligible` is normalized in the transform for the shared
 * CatalogPermanentDeleteModal.
 */
export interface GenrePurgePreflight extends CatalogPurgePreflight {
  livingSongCount?: number;
  subgenreCount?: number;
  blocked?: boolean;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
  details?: unknown;
}

function requireData<T>(raw: ApiEnvelope<T>, fallbackMessage: string): T {
  if (!raw || raw.success !== true || !raw.data) {
    throw new Error(raw && raw.message ? raw.message : fallbackMessage);
  }
  return raw.data;
}

/**
 * Normalize a taxonomy preflight payload onto the shared catalog preflight
 * shape: eligible falls back to !blocked, and livingSongCount/subgenreCount
 * become labelled count lines when the backend did not pre-label them.
 */
export function normalizeGenrePurgePreflight(
  raw: Partial<GenrePurgePreflight>,
  kind: "genre" | "subgenre",
): GenrePurgePreflight {
  const blocked = raw.blocked === true;
  const eligible =
    typeof raw.eligible === "boolean" ? raw.eligible : !blocked;
  const counts: CatalogPurgeCount[] = Array.isArray(raw.counts)
    ? (raw.counts as CatalogPurgeCount[])
    : [];
  const hasLabel = (label: string) =>
    counts.some((count) => count.label === label);
  if (typeof raw.livingSongCount === "number" && !hasLabel("Living songs referencing")) {
    counts.push({ label: "Living songs referencing", value: raw.livingSongCount });
  }
  if (
    kind === "genre" &&
    typeof raw.subgenreCount === "number" &&
    !hasLabel("Subgenres (also permanently deleted)")
  ) {
    counts.push({
      label: "Subgenres (also permanently deleted)",
      value: raw.subgenreCount,
    });
  }
  const blockers = Array.isArray(raw.blockers) ? raw.blockers : [];
  return {
    ...(raw as GenrePurgePreflight),
    kind,
    id: String(raw.id ?? ""),
    name: String(raw.name ?? ""),
    status: String(raw.status ?? "deleted"),
    dryRun: true,
    eligible,
    blockers:
      !eligible && blockers.length === 0
        ? [
            {
              code: "LIVING_SONGS",
              message:
                typeof raw.livingSongCount === "number"
                  ? `${raw.livingSongCount} living song(s) still reference this ${kind}.`
                  : `Living songs still reference this ${kind}.`,
            },
          ]
        : blockers,
    counts,
    willRemove:
      Array.isArray(raw.willRemove) && raw.willRemove.length > 0
        ? raw.willRemove
        : kind === "genre"
          ? [
              "This genre AND all of its subgenres (purging a genre also purges its subgenres)",
              "Visibility in genre pickers and Song Library filters",
            ]
          : [
              "This subgenre",
              "Visibility in subgenre pickers and Song Library filters",
            ],
    willRetain:
      Array.isArray(raw.willRetain) && raw.willRetain.length > 0
        ? raw.willRetain
        : ["Purge tombstone in append-only audit history"],
  };
}

const GENRES_LIST_TAG = { type: "Genres" as const, id: "LIST" };

/**
 * How long to wait before the post-create reconcile refetch. The genre
 * projection materializer normally lands well inside this window, so the
 * refetch confirms (rather than deletes) the locally patched row.
 */
const GENRES_PROJECTION_RECONCILE_MS = 6000;

function nameLowerCompare(a: { nameLower: string }, b: { nameLower: string }) {
  if (a.nameLower < b.nameLower) return -1;
  if (a.nameLower > b.nameLower) return 1;
  return 0;
}

function listArgIncludesStatus(
  arg: ListGenresArgs | void,
  status: GenreStatus,
): boolean {
  const wanted = (arg && arg.status) || "active";
  return wanted === "all" || wanted === status;
}

/** Reconcile with server truth once the projection has had time to settle. */
function scheduleGenresListReconcile(dispatch: (action: unknown) => unknown) {
  setTimeout(() => {
    dispatch(genresApi.util.invalidateTags([GENRES_LIST_TAG]));
  }, GENRES_PROJECTION_RECONCILE_MS);
}

/** Insert a freshly created Genre into every cached list it belongs in. */
function upsertCreatedGenre(
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  genre: Genre,
) {
  const cachedArgs = genresApi.util.selectCachedArgsForQuery(
    getState() as Parameters<typeof genresApi.util.selectCachedArgsForQuery>[0],
    "listGenres",
  );
  for (const arg of cachedArgs) {
    if (!listArgIncludesStatus(arg, genre.status)) continue;
    dispatch(
      genresApi.util.updateQueryData("listGenres", arg, (draft) => {
        if (draft.genres.some((row) => row.genreId === genre.genreId)) return;
        draft.genres.push({ ...genre, songCount: 0, subgenres: [] });
        draft.genres.sort(nameLowerCompare);
      }),
    );
  }
}

/** Insert a freshly created Subgenre under its parent in every cached list. */
function upsertCreatedSubgenre(
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  subgenre: Subgenre,
) {
  const cachedArgs = genresApi.util.selectCachedArgsForQuery(
    getState() as Parameters<typeof genresApi.util.selectCachedArgsForQuery>[0],
    "listGenres",
  );
  for (const arg of cachedArgs) {
    dispatch(
      genresApi.util.updateQueryData("listGenres", arg, (draft) => {
        const parent = draft.genres.find(
          (row) => row.genreId === subgenre.genreId,
        );
        if (!parent) return;
        const subs = parent.subgenres || (parent.subgenres = []);
        if (subs.some((row) => row.subgenreId === subgenre.subgenreId)) return;
        subs.push({ ...subgenre, songCount: 0 });
        subs.sort(nameLowerCompare);
      }),
    );
  }
}

/**
 * Apply an in-place recipe to every cached listGenres draft, then schedule
 * the delayed server-truth reconcile. Status-membership moves (e.g. an
 * archived genre leaving a status=active list) are left to the reconcile;
 * the immediate patch keeps the row itself truthful (name/status/removal)
 * so the management page reflects the mutation without a browser refresh.
 */
function patchAllGenreLists(
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  recipe: (draft: ListGenresResponse) => void,
) {
  const cachedArgs = genresApi.util.selectCachedArgsForQuery(
    getState() as Parameters<typeof genresApi.util.selectCachedArgsForQuery>[0],
    "listGenres",
  );
  for (const arg of cachedArgs) {
    dispatch(genresApi.util.updateQueryData("listGenres", arg, recipe));
  }
  scheduleGenresListReconcile(dispatch);
}

function patchGenreRow(
  draft: ListGenresResponse,
  genreId: string,
  patch: Partial<Genre>,
) {
  const row = draft.genres.find((g) => g.genreId === genreId);
  if (!row) return;
  Object.assign(row, patch);
  if (patch.nameLower) draft.genres.sort(nameLowerCompare);
}

/**
 * Archive/Restore change a genre's LIST MEMBERSHIP (status=active vs
 * status=deleted caches), not just a field. Move the row across every
 * cached list immediately so the lifecycle tab counts and both tab lists
 * react without waiting for the projection reconcile.
 */
function moveGenreAcrossLists(
  dispatch: (action: unknown) => unknown,
  getState: () => unknown,
  genreId: string,
  newStatus: GenreStatus,
  patch: Partial<Genre>,
) {
  const state = getState();
  const cachedArgs = genresApi.util.selectCachedArgsForQuery(
    state as Parameters<typeof genresApi.util.selectCachedArgsForQuery>[0],
    "listGenres",
  );
  // Capture the full row from any cache that has it, so it can be inserted
  // into lists it now belongs in (e.g. the Archived list after an archive).
  let row: Genre | undefined;
  for (const arg of cachedArgs) {
    const cached = genresApi.endpoints.listGenres.select(arg as never)(
      state as never,
    );
    const found = cached.data?.genres.find((g) => g.genreId === genreId);
    if (found) {
      row = found;
      break;
    }
  }
  for (const arg of cachedArgs) {
    dispatch(
      genresApi.util.updateQueryData("listGenres", arg, (draft) => {
        const idx = draft.genres.findIndex((g) => g.genreId === genreId);
        if (listArgIncludesStatus(arg, newStatus)) {
          if (idx >= 0) {
            Object.assign(draft.genres[idx], { ...patch, status: newStatus });
          } else if (row) {
            draft.genres.push({ ...row, ...patch, status: newStatus });
            draft.genres.sort(nameLowerCompare);
          }
        } else if (idx >= 0) {
          draft.genres.splice(idx, 1);
        }
      }),
    );
  }
  scheduleGenresListReconcile(dispatch);
}

function patchSubgenreRow(
  draft: ListGenresResponse,
  genreId: string,
  subgenreId: string,
  patch: Partial<Subgenre>,
) {
  const parent = draft.genres.find((g) => g.genreId === genreId);
  const row = (parent?.subgenres ?? []).find(
    (s) => s.subgenreId === subgenreId,
  );
  if (!parent || !row) return;
  Object.assign(row, patch);
  if (patch.nameLower && parent.subgenres) {
    parent.subgenres.sort(nameLowerCompare);
  }
}

export const genresApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    listGenres: builder.query<ListGenresResponse, ListGenresArgs | void>({
      query: (args) => {
        const params: Record<string, string | number | boolean> = {};
        if (args && args.status) params.status = args.status;
        if (args && typeof args.counts === "boolean") params.counts = args.counts;
        if (args && args.limit) params.limit = args.limit;
        if (args && args.pageToken) params.pageToken = args.pageToken;
        return { url: "/genres", method: "GET", params };
      },
      transformResponse: (raw: ApiEnvelope<ListGenresResponse>) =>
        requireData(raw, "Failed to load genres"),
      providesTags: (result) =>
        result && result.genres
          ? [
              ...result.genres.map((row) => ({
                type: "Genres" as const,
                id: row.genreId,
              })),
              GENRES_LIST_TAG,
            ]
          : [GENRES_LIST_TAG],
    }),
    createGenre: builder.mutation<Genre, { name: string }>({
      query: (body) => ({ url: "/genres", method: "POST", body }),
      transformResponse: (raw: ApiEnvelope<{ genre: Genre }>) =>
        requireData(raw, "Failed to create genre").genre,
      // No instant invalidation: the projection-backed list may not include
      // the new row yet. Patch caches from the canonical response, then
      // reconcile after the materializer settles.
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        const { data: genre } = await queryFulfilled;
        upsertCreatedGenre(dispatch, getState, genre);
        scheduleGenresListReconcile(dispatch);
      },
    }),
    renameGenre: builder.mutation<Genre, { genreId: string; name: string }>({
      query: ({ genreId, name }) => ({
        url: `/genres/${encodeURIComponent(genreId)}`,
        method: "PATCH",
        body: { name },
      }),
      transformResponse: (raw: ApiEnvelope<{ genre: Genre }>) =>
        requireData(raw, "Failed to rename genre").genre,
      async onQueryStarted({ genreId }, { dispatch, getState, queryFulfilled }) {
        const { data: genre } = await queryFulfilled;
        patchAllGenreLists(dispatch, getState, (draft) =>
          patchGenreRow(draft, genreId, {
            name: genre.name,
            nameLower: genre.nameLower,
            version: genre.version,
            updatedAt: genre.updatedAt,
          }),
        );
      },
    }),
    archiveGenre: builder.mutation<
      { genreId: string; status: GenreStatus; deletedAt: string; version: number },
      { genreId: string }
    >({
      query: ({ genreId }) => ({
        url: `/genres/${encodeURIComponent(genreId)}`,
        method: "DELETE",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          genreId: string;
          status: GenreStatus;
          deletedAt: string;
          version: number;
        }>,
      ) => requireData(raw, "Failed to archive genre"),
      async onQueryStarted({ genreId }, { dispatch, getState, queryFulfilled }) {
        const { data } = await queryFulfilled;
        moveGenreAcrossLists(dispatch, getState, genreId, data.status, {
          version: data.version,
        });
      },
    }),
    restoreGenre: builder.mutation<
      { genreId: string; status: "active"; restoredAt: string; version: number },
      { genreId: string }
    >({
      query: ({ genreId }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/restore`,
        method: "POST",
      }),
      transformResponse: (
        raw: ApiEnvelope<{
          genreId: string;
          status: "active";
          restoredAt: string;
          version: number;
        }>,
      ) => requireData(raw, "Failed to restore genre"),
      async onQueryStarted({ genreId }, { dispatch, getState, queryFulfilled }) {
        const { data } = await queryFulfilled;
        moveGenreAcrossLists(dispatch, getState, genreId, data.status, {
          version: data.version,
        });
      },
    }),
    preflightPurgeGenre: builder.mutation<GenrePurgePreflight, { genreId: string }>({
      query: ({ genreId }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/permanent-delete/preflight`,
        method: "POST",
        body: { dryRun: true },
      }),
      transformResponse: (raw: ApiEnvelope<GenrePurgePreflight>) =>
        normalizeGenrePurgePreflight(
          requireData(raw, "Failed to load genre deletion preflight"),
          "genre",
        ),
      // Dry run — nothing to invalidate.
      invalidatesTags: () => [],
    }),
    purgeGenre: builder.mutation<
      { genreId: string; name?: string; purged: boolean },
      { genreId: string; reason: string }
    >({
      query: ({ genreId, reason }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/permanent-delete`,
        method: "POST",
        body: { confirm: DELETE_CONFIRM_TOKEN, reason },
      }),
      transformResponse: (
        raw: ApiEnvelope<{ genreId: string; name?: string; purged: boolean }>,
      ) => requireData(raw, "Failed to permanently delete genre"),
      async onQueryStarted({ genreId }, { dispatch, getState, queryFulfilled }) {
        await queryFulfilled;
        patchAllGenreLists(dispatch, getState, (draft) => {
          draft.genres = draft.genres.filter((g) => g.genreId !== genreId);
        });
      },
    }),
    createSubgenre: builder.mutation<Subgenre, { genreId: string; name: string }>({
      query: ({ genreId, name }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/subgenres`,
        method: "POST",
        body: { name },
      }),
      transformResponse: (raw: ApiEnvelope<{ subgenre: Subgenre }>) =>
        requireData(raw, "Failed to create subgenre").subgenre,
      // Same projection-lag handling as createGenre: patch caches from the
      // canonical response, reconcile with a delayed refetch.
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        const { data: subgenre } = await queryFulfilled;
        upsertCreatedSubgenre(dispatch, getState, subgenre);
        scheduleGenresListReconcile(dispatch);
      },
    }),
    renameSubgenre: builder.mutation<
      Subgenre,
      { genreId: string; subgenreId: string; name: string }
    >({
      query: ({ genreId, subgenreId, name }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/subgenres/${encodeURIComponent(subgenreId)}`,
        method: "PATCH",
        body: { name },
      }),
      transformResponse: (raw: ApiEnvelope<{ subgenre: Subgenre }>) =>
        requireData(raw, "Failed to rename subgenre").subgenre,
      async onQueryStarted(
        { genreId, subgenreId },
        { dispatch, getState, queryFulfilled },
      ) {
        const { data: subgenre } = await queryFulfilled;
        patchAllGenreLists(dispatch, getState, (draft) =>
          patchSubgenreRow(draft, genreId, subgenreId, {
            name: subgenre.name,
            nameLower: subgenre.nameLower,
            version: subgenre.version,
            updatedAt: subgenre.updatedAt,
          }),
        );
      },
    }),
    archiveSubgenre: builder.mutation<
      { subgenreId: string; status: GenreStatus },
      { genreId: string; subgenreId: string }
    >({
      query: ({ genreId, subgenreId }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/subgenres/${encodeURIComponent(subgenreId)}`,
        method: "DELETE",
      }),
      transformResponse: (
        raw: ApiEnvelope<{ subgenreId: string; status: GenreStatus }>,
      ) => requireData(raw, "Failed to archive subgenre"),
      async onQueryStarted(
        { genreId, subgenreId },
        { dispatch, getState, queryFulfilled },
      ) {
        const { data } = await queryFulfilled;
        patchAllGenreLists(dispatch, getState, (draft) =>
          patchSubgenreRow(draft, genreId, subgenreId, { status: data.status }),
        );
      },
    }),
    restoreSubgenre: builder.mutation<
      { subgenreId: string; status: "active" },
      { genreId: string; subgenreId: string }
    >({
      query: ({ genreId, subgenreId }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/subgenres/${encodeURIComponent(subgenreId)}/restore`,
        method: "POST",
      }),
      transformResponse: (
        raw: ApiEnvelope<{ subgenreId: string; status: "active" }>,
      ) => requireData(raw, "Failed to restore subgenre"),
      async onQueryStarted(
        { genreId, subgenreId },
        { dispatch, getState, queryFulfilled },
      ) {
        const { data } = await queryFulfilled;
        patchAllGenreLists(dispatch, getState, (draft) =>
          patchSubgenreRow(draft, genreId, subgenreId, { status: data.status }),
        );
      },
    }),
    preflightPurgeSubgenre: builder.mutation<
      GenrePurgePreflight,
      { genreId: string; subgenreId: string }
    >({
      query: ({ genreId, subgenreId }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/subgenres/${encodeURIComponent(subgenreId)}/permanent-delete/preflight`,
        method: "POST",
        body: { dryRun: true },
      }),
      transformResponse: (raw: ApiEnvelope<GenrePurgePreflight>) =>
        normalizeGenrePurgePreflight(
          requireData(raw, "Failed to load subgenre deletion preflight"),
          "subgenre",
        ),
      invalidatesTags: () => [],
    }),
    purgeSubgenre: builder.mutation<
      { subgenreId?: string; name?: string; purged: boolean },
      { genreId: string; subgenreId: string; reason: string }
    >({
      query: ({ genreId, subgenreId, reason }) => ({
        url: `/genres/${encodeURIComponent(genreId)}/subgenres/${encodeURIComponent(subgenreId)}/permanent-delete`,
        method: "POST",
        body: { confirm: DELETE_CONFIRM_TOKEN, reason },
      }),
      transformResponse: (
        raw: ApiEnvelope<{ subgenreId?: string; name?: string; purged: boolean }>,
      ) => requireData(raw, "Failed to permanently delete subgenre"),
      async onQueryStarted(
        { genreId, subgenreId },
        { dispatch, getState, queryFulfilled },
      ) {
        await queryFulfilled;
        patchAllGenreLists(dispatch, getState, (draft) => {
          const parent = draft.genres.find((g) => g.genreId === genreId);
          if (!parent || !parent.subgenres) return;
          parent.subgenres = parent.subgenres.filter(
            (s) => s.subgenreId !== subgenreId,
          );
        });
      },
    }),
  }),
});

export const {
  useListGenresQuery,
  useCreateGenreMutation,
  useRenameGenreMutation,
  useArchiveGenreMutation,
  useRestoreGenreMutation,
  usePreflightPurgeGenreMutation,
  usePurgeGenreMutation,
  useCreateSubgenreMutation,
  useRenameSubgenreMutation,
  useArchiveSubgenreMutation,
  useRestoreSubgenreMutation,
  usePreflightPurgeSubgenreMutation,
  usePurgeSubgenreMutation,
} = genresApi;

export function extractGenresErrorMessage(err: unknown): string {
  if (!err) return "";
  if (typeof err === "string") return err;
  if (typeof err === "object") {
    const anyErr = err as Record<string, unknown>;
    const data = anyErr.data as Record<string, unknown> | undefined;
    if (data && typeof data.message === "string" && data.message.length > 0) {
      return data.message;
    }
    if (typeof anyErr.message === "string" && anyErr.message.length > 0) {
      return anyErr.message;
    }
    if (typeof anyErr.error === "string" && anyErr.error.length > 0) {
      return anyErr.error;
    }
    if (typeof anyErr.status === "number") {
      return `Request failed (HTTP ${anyErr.status})`;
    }
  }
  return "Request failed";
}

/** Active genres (with their active subgenres) for picker dropdowns. */
export function activeGenreOptions(genres: readonly Genre[] | undefined): Array<{
  genreId: string;
  name: string;
  subgenres: Array<{ subgenreId: string; name: string }>;
}> {
  return (genres ?? [])
    .filter((genre) => genre.status === "active")
    .map((genre) => ({
      genreId: genre.genreId,
      name: genre.name,
      subgenres: (genre.subgenres ?? [])
        .filter((sub) => sub.status === "active")
        .map((sub) => ({ subgenreId: sub.subgenreId, name: sub.name })),
    }));
}
